// Role-Based Access Control Service
// Provides security layer for all Firebase operations

import { 
  Permission, 
  PermissionChecker, 
  PermissionContext, 
  ResourceContext,
  UserRole,
  getPermissionForAction,
  ArticleAction,
  MediaAction,
  UserAction
} from '../types/permissions';
import { User, Article } from '../types/content';
import { MediaItem } from '../types/media';

export interface SecureOperationContext {
  currentUser: {
    id: string;
    role: UserRole;
    isActive: boolean;
  };
  action: ArticleAction | MediaAction | UserAction;
  resource?: ResourceContext;
}

export class RBACService {
  /**
   * Check if user can perform a specific action
   */
  static checkPermission(
    currentUser: User,
    permission: Permission,
    resource?: ResourceContext
  ): boolean {
    if (!currentUser || !currentUser.isActive) {
      return false;
    }

    const context: PermissionContext = {
      user: {
        id: currentUser.id!,
        role: currentUser.role,
        isActive: currentUser.isActive,
      },
      resource,
    };

    return PermissionChecker.canPerformAction(context, permission);
  }

  /**
   * Secure wrapper for operations - throws error if unauthorized
   */
  static async authorize(
    currentUser: User | null,
    permission: Permission,
    resource?: ResourceContext,
    errorMessage?: string
  ): Promise<void> {
    if (!currentUser) {
      throw new Error('Authentication required');
    }

    if (!this.checkPermission(currentUser, permission, resource)) {
      throw new Error(errorMessage || `Insufficient permissions for action: ${permission}`);
    }
  }

  /**
   * Check article-specific permissions
   */
  static checkArticlePermission(
    currentUser: User,
    action: 'create' | 'read' | 'edit' | 'delete' | 'publish',
    articleAuthorId?: string
  ): boolean {
    const articleAction: ArticleAction = {
      type: action,
      articleId: 'dummy', // Required to identify as ArticleAction in getPermissionForAction
      authorId: articleAuthorId,
    };

    // Normalize IDs for comparison
    const normalizedCurrentUserId = typeof currentUser.id === 'string' ? currentUser.id.trim() : currentUser.id;
    const normalizedAuthorId = typeof articleAuthorId === 'string' ? articleAuthorId.trim() : articleAuthorId;

    // Determine scope based on ownership
    const isOwnArticle = normalizedAuthorId === normalizedCurrentUserId;
    const scope = isOwnArticle ? 'own' : 'all';

    try {
      const permission = getPermissionForAction(articleAction, scope);
      const resource: ResourceContext = articleAuthorId ? { authorId: articleAuthorId } : {};
      
      const hasPermission = this.checkPermission(currentUser, permission, resource);
      return hasPermission;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check media-specific permissions
   */
  static checkMediaPermission(
    currentUser: User,
    action: 'upload' | 'read' | 'edit' | 'delete',
    mediaUploadedBy?: string
  ): boolean {
    const mediaAction: MediaAction = {
      type: action,
      mediaId: 'dummy', // Required to identify as MediaAction in getPermissionForAction
      uploadedBy: mediaUploadedBy,
    };

    // Determine scope based on ownership
    const isOwnMedia = mediaUploadedBy === currentUser.id;
    const scope = isOwnMedia ? 'own' : 'all';

    try {
      const permission = getPermissionForAction(mediaAction, scope);
      const resource: ResourceContext = mediaUploadedBy ? { uploadedBy: mediaUploadedBy } : {};
      
      return this.checkPermission(currentUser, permission, resource);
    } catch (error) {
      return false;
    }
  }

  /**
   * Check user management permissions
   */
  static checkUserPermission(
    currentUser: User,
    action: 'create' | 'read' | 'edit' | 'delete' | 'change_role' | 'activate',
    targetUserId?: string
  ): boolean {
    const userAction: UserAction = {
      type: action,
      targetUserId,
    };

    try {
      const permission = getPermissionForAction(userAction);
      const resource: ResourceContext = targetUserId ? { ownerId: targetUserId } : {};
      
      return this.checkPermission(currentUser, permission, resource);
    } catch (error) {
      return false;
    }
  }

  /**
   * Filter articles based on user permissions
   */
  static filterArticlesByPermission(
    articles: Article[],
    currentUser: User | null
  ): Article[] {
    if (!currentUser) {
      // Non-authenticated users can only see published articles
      return articles.filter(article => article.status === 'published');
    }

    // Admins and owners can see all articles
    if (currentUser.role === 'admin' || currentUser.role === 'owner') {
      return articles;
    }

    // Editors: Handled by getArticlesForEditor() in content-service.ts
    // That method does proper tag ID to slug conversion and filtering
    if (currentUser.role === 'editor') {
      return articles; // Already filtered by getArticlesForEditor
    }

    // Writers can only see published articles and their own drafts
    const normalizedCurrentUserId = typeof currentUser.id === 'string' ? currentUser.id.trim() : currentUser.id;
    
    return articles.filter(article => {
      if (article.status === 'published') return true;
      
      // Check if this is the writer's own article (for drafts)
      const normalizedAuthorId = typeof article.author === 'string' ? article.author.trim() : article.author;
      return normalizedAuthorId === normalizedCurrentUserId;
    });
  }

  /**
   * Filter media based on user permissions
   */
  static filterMediaByPermission(
    mediaItems: MediaItem[],
    currentUser: User | null
  ): MediaItem[] {
    if (!currentUser) {
      return []; // No media access for non-authenticated users
    }

    // Admin, owners, and editors can see all media
    if (currentUser.role === 'admin' || currentUser.role === 'owner' || currentUser.role === 'editor') {
      return mediaItems;
    }

    // Writers can only see their own uploads
    const normalizedCurrentUserId = typeof currentUser.id === 'string' ? currentUser.id.trim() : currentUser.id;
    
    return mediaItems.filter(item => {
      const normalizedUploaderId = typeof item.uploadedBy === 'string' ? item.uploadedBy.trim() : item.uploadedBy;
      return normalizedUploaderId === normalizedCurrentUserId;
    });
  }

  /**
   * Get allowed article statuses for user
   */
  static getAllowedArticleStatuses(currentUser: User): ('draft' | 'published')[] {
    if (!currentUser || !currentUser.isActive) {
      return [];
    }

    const statuses: ('draft' | 'published')[] = ['draft'];

    // Only editors and admins can publish
    if (this.checkPermission(currentUser, Permission.ARTICLE_PUBLISH_ALL)) {
      statuses.push('published');
    }

    return statuses;
  }

  /**
   * Check if user can access admin dashboard
   */
  static canAccessAdminDashboard(currentUser: User | null): boolean {
    if (!currentUser) return false;
    return this.checkPermission(currentUser, Permission.ADMIN_MENU_ACCESS);
  }

  /**
   * Check if user can access editor dashboard
   */
  static canAccessEditorDashboard(currentUser: User | null): boolean {
    if (!currentUser) return false;
    return this.checkPermission(currentUser, Permission.EDITOR_MENU_ACCESS) ||
           this.checkPermission(currentUser, Permission.ADMIN_MENU_ACCESS);
  }

  /**
   * Validate and sanitize article form data based on user permissions
   * Note: Uses `any` to support both ArticleFormData and Partial<Article> types
   */
  static validateArticleFormData(
    currentUser: User,
    // @ts-ignore - Supporting both ArticleFormData and Partial<Article>
    formData: any,
    isUpdate: boolean = false,
    existingAuthor?: string
  // @ts-ignore - Supporting both ArticleFormData and Partial<Article>
  ): any {
    const sanitizedData = { ...formData };

    // Handle author assignment - ALWAYS use user ID, not display name
    if (isUpdate) {
      // During update, check if the user can assign articles
      if (currentUser.role === 'writer') {
        // Writers cannot reassign articles - keep existing author
        sanitizedData.author = existingAuthor;
        // Writers can only save as draft when editing their own articles
        if (sanitizedData.status === 'published') {
          sanitizedData.status = 'draft';
        }
      } else if (currentUser.role === 'editor' || currentUser.role === 'admin' || currentUser.role === 'owner') {
        // Editors, admins, and owners can assign articles to anyone and keep any status
        // If no author specified in form data, keep existing
        if (!sanitizedData.author) {
          sanitizedData.author = existingAuthor;
        }
        // Admins/editors can assign published articles to writers and keep them published
        // No status restriction during assignment
      }
    } else {
      // During creation - CRITICAL: Always use user ID for author field
      if (currentUser.role === 'writer') {
        // Writers create articles for themselves - use ID, not display name
        sanitizedData.author = currentUser.id!;
        // Writers cannot publish during creation - only draft
        if (sanitizedData.status === 'published') {
          sanitizedData.status = 'draft';
        }
      } else if (currentUser.role === 'editor' || currentUser.role === 'admin' || currentUser.role === 'owner') {
        // Editors, admins, and owners can create articles for anyone
        // If no author specified, assign to themselves using ID
        if (!sanitizedData.author) {
          sanitizedData.author = currentUser.id!;
        }
        // Editors/admins can publish during creation
      }
    }

    return sanitizedData;
  }

  /**
   * Build Firestore query constraints based on user permissions
   */
  static getArticleQueryConstraints(currentUser: User | null): Array<[string, ...unknown[]]> {
    const constraints: Array<[string, ...unknown[]]> = [];

    if (!currentUser) {
      // Non-authenticated users only see published articles
      constraints.push(['where', 'status', '==', 'published']);
    } else if (currentUser.role === 'writer') {
      // Writers see published articles and their own drafts
      constraints.push([
        'or',
        ['where', 'status', '==', 'published'],
        ['and', ['where', 'author', '==', currentUser.id], ['where', 'status', '==', 'draft']]
      ]);
    }
    // Editors and admins see all articles (no additional constraints)

    return constraints;
  }

  /**
   * Build Firestore query constraints for media based on user permissions
   */
  static getMediaQueryConstraints(currentUser: User | null): Array<[string, ...unknown[]]> {
    const constraints: Array<[string, ...unknown[]]> = [];

    if (!currentUser) {
      // No media access for non-authenticated users
      constraints.push(['where', 'uploadedBy', '==', 'nobody']); // Impossible condition
    } else if (currentUser.role === 'writer') {
      // Writers only see their own uploads
      constraints.push(['where', 'uploadedBy', '==', currentUser.id]);
    }
    // Editors and admins see all media (no additional constraints)

    return constraints;
  }

  /**
   * Check if user can perform bulk operations
   */
  static canPerformBulkOperations(currentUser: User | null): boolean {
    if (!currentUser) return false;
    
    // Only editors, admins, and owners can perform bulk operations
    return currentUser.role === 'editor' || currentUser.role === 'admin' || currentUser.role === 'owner';
  }

  /**
   * Generate error messages for permission failures
   */
  static getPermissionErrorMessage(
    permission: Permission,
    userRole?: UserRole
  ): string {
    const roleText = userRole ? `as a ${userRole}` : '';
    
    switch (permission) {
      case Permission.ARTICLE_PUBLISH_ALL:
      case Permission.ARTICLE_PUBLISH_OWN:
        return `You don't have permission to publish articles ${roleText}. Only editors and admins can publish content.`;
      
      case Permission.ARTICLE_EDIT_ALL:
        return `You can only edit your own articles ${roleText}. Contact an editor to modify other articles.`;
      
      case Permission.USER_CREATE:
      case Permission.USER_DELETE:
      case Permission.USER_CHANGE_ROLES:
        return `Only administrators can manage users ${roleText}.`;
      
      case Permission.MEDIA_READ_ALL:
      case Permission.MEDIA_EDIT_ALL:
        return `You can only access your own media uploads ${roleText}.`;
      
      case Permission.ADMIN_MENU_ACCESS:
        return `Admin access required ${roleText}.`;
      
      case Permission.EDITOR_MENU_ACCESS:
        return `Editor or admin access required ${roleText}.`;
      
      default:
        return `Insufficient permissions ${roleText} for this action.`;
    }
  }

  /**
   * Debug helper to log full permission check details
   */
  static debugPermissionCheck(
    currentUser: User,
    action: string,
    articleAuthorId?: string,
    context?: string
  ): void {
    if (process.env.NODE_ENV !== 'development') return;
    
    console.group(`🔐 RBAC Debug: ${context || action}`);
    if (articleAuthorId) {
      }
    
    // Test different permission scopes
    if (action === 'publish') {
      const canPublishOwn = this.checkPermission(currentUser, Permission.ARTICLE_PUBLISH_OWN);
      const canPublishAll = this.checkPermission(currentUser, Permission.ARTICLE_PUBLISH_ALL);
      }
    
    console.groupEnd();
  }
}

// Export a configured instance
export const rbacService = new RBACService();

